import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Chip, GalleryDiorama } from '@/design-system'
import { SCENE_PRESET_GEOMETRY } from '@/features/gallery-studio-3d/constants'
import { getClassicFamilyForId } from '@/features/gallery-studio-3d/classicCatalog'
import { GallerySummary } from '../../types'
import { formatMonthYear, formatRelative } from '../../utils/date'
import { CLASSIC_ROOM_TINT } from '../../utils/roomColor'

export interface GalleryListRowProps {
  gallery: GallerySummary
  onClick?: () => void
  sourceAlbumCoverUrl?: string
  sourceAlbumTitle?: string
}

const Row = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '64px 1.6fr 130px 100px 120px 90px',
  alignItems: 'center',
  gap: 14,
  padding: '10px 16px',
  borderBottom: '1px solid var(--ds-border)',
  cursor: 'pointer',
  transition: `background var(--ds-duration-fast) var(--ds-ease)`,
  '&:last-of-type': { borderBottom: 'none' },
  '&:hover': { background: 'var(--ds-surface)' },
  '@media (max-width: 760px)': {
    gridTemplateColumns: '52px 1fr 90px',
    '& .glr-scene, & .glr-updated': { display: 'none' },
  },
})

const Thumb = styled(Box)({
  width: 64,
  height: 46,
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-soft)',
  flexShrink: 0,
  backgroundColor: 'var(--ds-surface)',
})

const TitleName = styled(Typography)({
  fontSize: 13.5,
  fontWeight: 650,
  letterSpacing: '-0.01em',
  color: 'var(--ds-ink)',
})

const TitleSub = styled(Typography)({
  fontSize: 11,
  color: 'var(--ds-ink-subtle)',
  marginTop: 2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const Stat = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 11,
  color: 'var(--ds-ink-muted)',
})

const Updated = styled(Typography)({
  fontSize: 11.5,
  color: 'var(--ds-ink-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
})

export function GalleryListRow({ gallery, onClick, sourceAlbumCoverUrl, sourceAlbumTitle }: GalleryListRowProps) {
  const isClassic = gallery.mode === 'classic'
  const geo = SCENE_PRESET_GEOMETRY[gallery.scenePreset || 'corridor']
  const fallbackTint = isClassic ? CLASSIC_ROOM_TINT : geo
  const wallColor = gallery.wallColor || fallbackTint.wallColor
  const floorColor = gallery.floorColor || fallbackTint.floorColor
  const arts = gallery.arts.length > 0 ? gallery.arts : sourceAlbumCoverUrl ? [{ img: sourceAlbumCoverUrl }] : []
  const sceneLabel = isClassic
    ? getClassicFamilyForId(gallery.classicTemplateId ?? -1)?.label || 'Classic'
    : geo.label
  const relative = formatRelative(gallery.updatedAt)
  const monthYear = formatMonthYear(gallery.updatedAt)

  return (
    <Row onClick={onClick} role='button' tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick?.()}>
      <Thumb>
        <GalleryDiorama wallColor={wallColor} floorColor={floorColor} arts={arts} size='sm' />
      </Thumb>
      <Box sx={{ minWidth: 0 }}>
        <TitleName noWrap>{gallery.title}</TitleName>
        <TitleSub>
          {sourceAlbumTitle ? `From ${sourceAlbumTitle} ↗` : relative ? `Updated ${relative}` : 'Not updated yet'}
        </TitleSub>
      </Box>
      <Stat className='glr-scene'>{sceneLabel}</Stat>
      <Stat>{gallery.printCount} print{gallery.printCount === 1 ? '' : 's'}</Stat>
      <Updated className='glr-updated'>{monthYear || relative || '—'}</Updated>
      <Box sx={{ textAlign: 'right' }}>
        {gallery.privacy === 'private' ? (
          <Chip tone='neutral' sx={{ height: 22, fontSize: 10.5 }}>
            <LockOutlinedIcon sx={{ fontSize: 11 }} /> Private
          </Chip>
        ) : (
          <Chip tone='accent' sx={{ height: 22, fontSize: 10.5 }}>
            {isClassic ? '◇' : '◈'} Walkable
          </Chip>
        )}
      </Box>
    </Row>
  )
}
